import { CampfireRichEditor } from '@campfire/campfire-rich-editor/lib';
import { FileUploadGallery, UploadedFile } from '@campfire/file-upload-gallery';
import { Box, Typography } from '@material-ui/core';
import { KeyboardArrowLeft } from '@material-ui/icons';
import { HoverLink } from '@campfire/hover-link';
import { convertFromRaw, EditorState } from 'draft-js';
import React, { useMemo } from 'react';
import { useParams, useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { useSupportContext } from '../../SupportContext';
import { SupportResource } from '../../__generated__/SupportResource';
import { AdminEditButtons } from './AdminEditButtons';
import { ResourcesListItem } from '../../catalogue-landing-page/ResourcesListItem';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

interface Props {
  selectedResource: SupportResource;
  returnSlug?: string;
}

const RELATED_RESOURCES_LIMIT = 3;

const ResourceViewer = (props: Props) => {
  const { selectedResource, returnSlug } = props;
  const { categoryId, resourceId } = useParams();
  const { allCategories } = useSupportContext();
  const { isMobile, isSm } = useCampfireTheme();
  const location = useLocation();
  const match = useRouteMatch();

  const selectedCategory = useMemo(() => allCategories.find((c) => c.supportCategoryId === categoryId), [
    categoryId,
    allCategories,
  ]);

  const relatedResources = useMemo(
    () => selectedCategory?.supportResources.filter((r) => r.supportResourceId !== resourceId) ?? [],
    [selectedCategory, resourceId]
  );

  const uploadedFiles: Array<UploadedFile> = useMemo(
    () =>
      selectedResource.supportResourceAttachments.map((attachment) => ({
        url: attachment.url,
        fileId: attachment.supportResourceAttachmentId,
        name: attachment.name,
      })),
    [selectedResource]
  );

  const draftJsContent = selectedResource.textContent
    ? convertFromRaw(JSON.parse(selectedResource.textContent))
    : undefined;

  const backUrl = returnSlug ?? (resourceId ? location.pathname.replace(`/${resourceId}`, '') : location.pathname);

  return (
    <Box display='flex' flexDirection='column' flex={1} height='100%' overflow='auto'>
      {/* Top bar with back link and admin actions */}
      <Box
        style={{
          paddingLeft: isMobile || isSm ? 10 : 25,
          paddingRight: 10,
          paddingTop: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <HoverLink to={backUrl} color='textPrimary' hoverColor='textPrimary' disableUnderline>
          <Box display='flex' alignItems='center'>
            <KeyboardArrowLeft style={{ fontSize: 20, color: '#666' }} />
            <Typography style={{ fontSize: 14, fontWeight: 600, color: '#666' }}>
              {selectedCategory ? `Back to ${selectedCategory.title}` : 'Back'}
            </Typography>
          </Box>
        </HoverLink>
        <AdminEditButtons supportCategoryId={selectedCategory?.supportCategoryId} />
      </Box>

      {/* Resource title */}
      <Box
        style={{
          paddingLeft: isMobile || isSm ? 10 : 60,
          paddingRight: isMobile || isSm ? 10 : 60,
          paddingTop: 24,
          paddingBottom: 24,
        }}
      >
        <Typography style={{ fontSize: 24, fontWeight: 700, color: '#333' }}>{selectedResource.title}</Typography>
      </Box>

      {/* Content area */}
      <Box
        style={{
          paddingLeft: isMobile || isSm ? 12 : 60,
          paddingRight: isMobile || isSm ? 12 : 60,
          paddingBottom: 40,
        }}
      >
        {/* Main content card */}
        <Box
          style={{
            border: '1px solid #e8e8e8',
            borderRadius: 12,
            overflow: 'hidden',
            padding: isMobile || isSm ? 20 : 32,
          }}
        >
          {/* Rich text content */}
          {draftJsContent && draftJsContent.hasText() ? (
            <Box style={{ overflow: 'hidden', marginBottom: 24 }}>
              <CampfireRichEditor editorState={EditorState.createWithContent(draftJsContent)} />
            </Box>
          ) : null}

          {/* Attachments */}
          {uploadedFiles.length > 0 ? (
            <Box>
              <Typography style={{ fontWeight: 700, fontSize: 16, color: '#333', marginBottom: 12 }}>
                {'Attachments'}
              </Typography>
              <FileUploadGallery allowMultiple uploadedFiles={uploadedFiles} viewOnly />
            </Box>
          ) : null}

          {/* Empty state */}
          {(!draftJsContent || !draftJsContent.hasText()) && uploadedFiles.length === 0 ? (
            <Typography style={{ fontSize: 14, color: '#999' }}>{'This resource has no content yet.'}</Typography>
          ) : null}
        </Box>

        {/* More from category */}
        {relatedResources.length > 0 ? (
          <Box style={{ marginTop: 30 }}>
            <Typography
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#999',
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: 12,
              }}
            >
              {`More from ${selectedCategory?.title}`}
            </Typography>
            <Box
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              {relatedResources.slice(0, RELATED_RESOURCES_LIMIT).map((resource, idx) => (
                <Link
                  data-track='resources-resource-viewer-see-more-list-item'
                  key={resource.supportResourceId}
                  to={{
                    pathname: match?.url.replace(`${resourceId}`, `${resource.supportResourceId}`) ?? '',
                    state: { returnSlug },
                  }}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <ResourcesListItem
                    resource={resource}
                    borderBottom={idx !== Math.min(relatedResources.length, RELATED_RESOURCES_LIMIT) - 1}
                    showUploadDate
                  />
                </Link>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export { ResourceViewer };
